import api from './api';
import { API_ENDPOINTS } from '../constants';
import { ApiResponse } from '../types';

export interface PerformerUser {
  id: string;
  first_name: string;
  last_name: string;
  employee_id: string;
  full_name: string;
  profile_image_url?: string;
  job_title?: string;
  store?: {
    id: string;
    name: string;
    code: string;
  };
  cluster?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface Performer {
  id: string;
  user_id: string;
  store_id?: string;
  week_start_date: string;
  week_end_date: string;
  score: number;
  description?: string;
  user: PerformerUser;
  store?: {
    id: string;
    name: string;
    code: string;
  };
}

const performerService = {
  getPerformerOfTheWeek: async (storeId?: string): Promise<Performer | null> => {
    try {
      const params: any = {};
      if (storeId) {
        params.store_id = storeId;
      }

      const response = await api.get<ApiResponse<Performer>>(API_ENDPOINTS.PERFORMERS.WEEK, {
        params,
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch (error: any) {
      // If 404 or no performer, return null
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Get performer of the week error:', error);
      throw error;
    }
  },

  updatePerformer: async (performerId: string, data: {
    score?: number;
    description?: string;
    is_active?: boolean;
  }): Promise<Performer> => {
    try {
      const response = await api.put<ApiResponse<Performer>>(
        API_ENDPOINTS.PERFORMERS.UPDATE(performerId),
        data
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update performer');
    } catch (error: any) {
      console.error('Update performer error:', error);
      throw error;
    }
  },

  createPerformer: async (data: {
    user_id: string;
    store_id?: string | null;
    score?: number;
    description?: string;
    week_start_date?: string;
    week_end_date?: string;
  }): Promise<Performer> => {
    try {
      const response = await api.post<ApiResponse<Performer>>(
        API_ENDPOINTS.PERFORMERS.CREATE,
        data
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create performer');
    } catch (error: any) {
      console.error('Create performer error:', error);
      throw error;
    }
  },

  deletePerformer: async (performerId: string): Promise<void> => {
    try {
      await api.delete(API_ENDPOINTS.PERFORMERS.DELETE(performerId));
    } catch (error: any) {
      console.error('Delete performer error:', error);
      throw error;
    }
  },
};

export default performerService;

